import { useSelector, useDispatch } from "react-redux";
import store, { RootState } from "../store/store";
import { clearTokens, setTokens, updateAccessToken } from "../store/token/tokenReducer";
import TOKEN_API from "../services/token";
import { setExchangeAuthData, setIsLoggedIn, setLogout, setSportBookAuthData } from "../store/authReducer";
import { dispatch } from "../store/store";
import { toast } from "react-toastify";
import { useState } from "react";
import { setIsCashoutEnabled, setTurboCashoutCommission } from "../store/exchangeSports/exchangeSportsReducer";

export const useAuth = () => {
    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth);
    const [isBlocked, setIsBlocked] = useState<boolean>(false);

    const logout = () => {
        dispatch(clearTokens());
    };

    const updateToken = (newAccessToken: string) => {
        dispatch(updateAccessToken(newAccessToken));
    };

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const initializeAuth = async (tgInitDataRaw: string, userKey: string): Promise<{ success: boolean; token: string | null }> => {
        try {
            const response = await TOKEN_API.post(
                `/api/v1/auth/auth-login`,
                {},
                {
                    headers: {
                        Authorization: `tma ${tgInitDataRaw}`,
                        userKey: userKey,
                    },
                }
            );

            if (response.status === 401) {
                dispatch(setLogout(true));
                logout();
                return { success: false, token: null };
            }

            if (response.data.status === "RS_OK") {
                if (response.data.userStatus === "BLOCKED") {
                sessionStorage.setItem("user_blocked", "true");
                setIsBlocked(true);
                }
                const accessToken = response.data?.accessToken;
                const refreshToken = response.data?.refreshToken;
                const userKey = response.data?.userKey;

                if (accessToken && refreshToken) {
                    // 1. Store
                    sessionStorage.setItem("token", accessToken);
                    sessionStorage.setItem("rtoken", refreshToken);
                    sessionStorage.setItem("userKey", userKey || "");
                    sessionStorage.setItem("isDemo", "false");

                    // 2. Retry fetch & compare logic
                    let attempts = 0;
                    const maxAttempts = 3;
                    let matched = false;

                    while (attempts < maxAttempts) {
                        const storedAccessToken = sessionStorage.getItem("token");
                        const storedRefreshToken = sessionStorage.getItem("rtoken");
                        const storedUserKey = sessionStorage.getItem("userKey");

                        if (storedAccessToken === accessToken && storedRefreshToken === refreshToken && storedUserKey === userKey) {
                            matched = true;
                            break;
                        }

                        attempts++;
                        await delay(100); // Wait 100ms before retrying
                    }

                    if (matched) {
                        dispatch(setTokens({ accessToken, refreshToken }));
                        return { success: true, token: accessToken };
                    } else {
                        console.warn("Token mismatch after retries: Possible storage failure", {
                            expected: accessToken,
                            stored: sessionStorage.getItem("token"),
                        });

                        sessionStorage.removeItem("token");
                        sessionStorage.removeItem("rtoken");
                        dispatch(setLogout(true));
                        logout();

                        return { success: false, token: null };
                    }
                }
            }
            return { success: false, token: null };
        } catch (error: any) {
            console.error("Error while authenticating user: ", error);

            if (error?.response?.status === 401) {
                dispatch(setLogout(true));
                logout();
            }

            return { success: false, token: null };
        }
    };

    const refreshToken = async () => {
        const accTkn = sessionStorage.getItem("rtoken");
        try {
            const response = await TOKEN_API.post(
                `/api/v1/auth/refresh-token`,
                {}, // Empty request body since headers are needed
                {
                    headers: {
                        Authorization: `Bearer ${accTkn}`,
                    }
                }
            );

            if (response.status === 401) {
                dispatch(setLogout(true));
                logout();
            }

            if (response.data.status === "RS_OK") {
                const responseData = response.data?.accessToken;
                if (responseData) {
                    sessionStorage.setItem("token", response.data?.accessToken || "");
                    sessionStorage.setItem("rtoken", response.data?.refreshToken || "");
                    dispatch(
                        setTokens({
                            accessToken: response?.data?.accessToken || null,
                            refreshToken: response?.data?.refreshToken || null,
                        })
                    );
                    return true;
                }
            }
            return false;
        } catch (error: any) {
            console.log("Error while authenticating user: ", error);
            if (error?.response?.status === 401) {
                dispatch(setLogout(true));
                logout();
            }
            return false;
        }
    };


    return {
        ...auth,
        logout,
        updateToken,
        initializeAuth,
        refreshToken,
        isBlocked,
    };
};

export const refreshAuthToken = async () => {
    const logout = () => {
        dispatch(clearTokens());
    };
    const state = store.getState(); // Get the current state
    const accTkn = state.auth.refreshToken;

    try {
        const response = await TOKEN_API.post(
            `/api/v1/auth/refresh-token`,
            {},
            {
                headers: { Authorization: `Bearer ${accTkn}` },
            }
        );

        if (response.status === 401) {
            dispatch(setLogout(true));
            logout();
        }

        if (response.data.status === "RS_OK") {
            sessionStorage.setItem("token", response.data?.accessToken || "");
            sessionStorage.setItem("rtoken", response.data?.refreshToken || "");
            dispatch(
                setTokens({
                    accessToken: response?.data?.accessToken || null,
                    refreshToken: response?.data?.refreshToken || null,
                })
            );
            return true;
        }
    } catch (error: any) {
        console.log("Error while authenticating user: ", error);
        return false;
    }
};

const emptyAuthData = {
    providerId: "",
    token: "",
    operatorId: "",
    partnerId: "",
    currency: "",
    rate: 0,
  };

export const logoutSession = () => {
    const tg = (window as any)?.Telegram?.WebApp;
    tg?.ready();

    const isTG = !!tg?.initDataUnsafe?.user;
    dispatch(clearTokens());
    dispatch(setLogout(true));
    dispatch(setIsCashoutEnabled(false));
    dispatch(setTurboCashoutCommission(0));
    dispatch(setExchangeAuthData(emptyAuthData));
    dispatch(setSportBookAuthData(emptyAuthData));
    sessionStorage.removeItem("isDemo");

    if (!isTG) {
        sessionStorage.setItem("isLoggedIn", "false");
        dispatch(setIsLoggedIn(false));
        window.location.replace('/login');
    }
}

